import {
    REGISTRABLE_PROGRAMME_OPTIONS,
    gradesForProgramme,
    groupBoardGradeKeys,
    toBoardGradeKeys,
    type BoardGrade,
} from '@padcanvas/shared';

/**
 * Grade × board, edited as rows and stored as the flat key vocabulary.
 *
 * The keys — 'cbse:10', 'jee', 'jee:11' — are what questions, simulations and
 * guided experiments all store, so nothing here invents a second format.
 * `groupBoardGradeKeys` and `toBoardGradeKeys` are the conversion and they
 * round-trip; one row per board is the shape they group into.
 *
 * "Whole board" is `boardWide` — a bare key like 'jee' with no grade. It is
 * independent of the grades beside it: an exam track normally carries both
 * ('jee' AND 'jee:11'), and dropping either half on a UI round-trip silently
 * changes what the record serves. That is why it is a checkbox of its own
 * rather than something inferred from an empty grade list.
 */
export function BoardGradeEditor({
    value,
    onChange,
    help,
}: {
    /** Flat keys, exactly as stored. */
    value: string[];
    onChange: (keys: string[]) => void;
    help?: React.ReactNode;
}) {
    const rows = groupBoardGradeKeys(value);
    const emit = (next: BoardGrade[]) => onChange(toBoardGradeKeys(next));
    const patch = (i: number, change: Partial<BoardGrade>) =>
        emit(rows.map((r, idx) => (idx === i ? { ...r, ...change } : r)));

    const unused = REGISTRABLE_PROGRAMME_OPTIONS.filter((b) => !rows.some((r) => r.board === b));

    return (
        <div className="form-group">
            <label>Grade × Board</label>
            {help && <p className="form-help">{help}</p>}
            <div className="gradeboards-editor">
                {rows.map((row, i) => (
                    <div key={row.board} className="gradeboard-row">
                        <span className="gradeboard-grade">{row.board}</span>
                        <div className="checkbox-group gradeboard-exams">
                            <label className="checkbox-label" title="Every grade this board teaches">
                                <input
                                    type="checkbox"
                                    checked={!!row.boardWide}
                                    onChange={() => patch(i, { boardWide: !row.boardWide })}
                                />
                                Whole board
                            </label>
                            {gradesForProgramme(row.board).map((g) => (
                                <label key={g} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={(row.grades ?? []).includes(g)}
                                        onChange={() =>
                                            patch(i, {
                                                grades: (row.grades ?? []).includes(g)
                                                    ? (row.grades ?? []).filter((x) => x !== g)
                                                    : [...(row.grades ?? []), g],
                                            })
                                        }
                                    />
                                    {g}
                                </label>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="btn-remove-instruction"
                            title={`Remove ${row.board}`}
                            onClick={() => emit(rows.filter((_, idx) => idx !== i))}
                        >×</button>
                    </div>
                ))}
                {unused.length > 0 && (
                    <select
                        className="gradeboard-grade"
                        value=""
                        onChange={(e) => e.target.value && emit([...rows, { board: e.target.value as BoardGrade['board'], grades: [] }])}
                    >
                        <option value="">+ Add board…</option>
                        {unused.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                )}
            </div>
        </div>
    );
}
